import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreateChancel, SimpleTabs } from 'components';
import { DrawStaff } from './drawStaff';
import { TypeStaff } from './typeStaff';
import { SignatureFileUploader } from 'fragments/schedule/core/common/signatureFileUploader';
import { signatureTabsLabels, tabEnums } from './constants';
import { base64ToFile, FindLoad, FindSuccessItem, useModal, KeyPurpose } from 'utils';
import { adminActions, appointmentActions, httpRequestsOnSuccessActions, uploadFile } from 'store';

const ACTION_TYPE = 'CREATE_STAFF_SIGNATURE';

export const AddSignature = ({ deleteSignature }) => {
    const { staffGeneral } = useSelector((state) => ({
        staffGeneral: state.admins.adminInfoById,
    }));
    const [currentFile, setCurrentFile] = useState(null);
    const [providerTab, setProviderTab] = useState(tabEnums.DRAW);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [typedName, setTypedName] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const sigCanvas = useRef(null);
    const dispatch = useDispatch();
    const loadingDraw = FindLoad(ACTION_TYPE);
    const successDraw = FindSuccessItem(ACTION_TYPE);
    const { close } = useModal();

    const disabled =
        providerTab === tabEnums.DRAW
            ? !(sigCanvas?.current && sigCanvas.current.getTrimmedCanvas().toDataURL('image/png') && isDrawing)
            : providerTab === tabEnums.UPLOAD
            ? !uploadedFile
            : !typedName.trim();

    useEffect(() => {
        if (successDraw?.type) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            close();
        }
    }, [successDraw?.type]);

    useEffect(() => {
        if (staffGeneral?.signature) {
            setCurrentFile({ file: staffGeneral?.signature });
        }
    }, [staffGeneral?.signature]);

    const generateTypedSignature = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 480;
        canvas.height = 120;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = "italic 40px 'Dancing Script', 'Brush Script MT', cursive";
        ctx.fillStyle = '#172B4D';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedName, 20, 60);
        return canvas.toDataURL('image/png');
    };

    const saveDraw = async () => {
        const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
        if (sigCanvas?.current && signatureData && isDrawing) {
            const convertedFile = base64ToFile(signatureData, 'signature.png');
            if (convertedFile) {
                try {
                    setUploadLoader(true);
                    const storedFile = await uploadFile(convertedFile, KeyPurpose.SIGNATURE);
                    dispatch(adminActions.createStaffSignature({ file: storedFile }, staffGeneral?.id));
                    setUploadLoader(false);
                } catch (e) {
                    setUploadLoader(false);
                }
            }
        }
    };

    const saveTyped = async () => {
        if (!typedName.trim()) return;
        const signatureData = generateTypedSignature();
        const convertedFile = base64ToFile(signatureData, 'signature.png');
        if (convertedFile) {
            try {
                setUploadLoader(true);
                const storedFile = await uploadFile(convertedFile, KeyPurpose.SIGNATURE);
                dispatch(adminActions.createStaffSignature({ file: storedFile }, staffGeneral?.id));
                setUploadLoader(false);
            } catch (e) {
                setUploadLoader(false);
            }
        }
    };

    const saveUpload = async () => {
        if (!uploadedFile) return;
        try {
            setUploadLoader(true);
            const storedFile = await uploadFile(uploadedFile, KeyPurpose.SIGNATURE);
            dispatch(adminActions.createStaffSignature({ file: storedFile }, staffGeneral?.id));
            setUploadLoader(false);
        } catch (e) {
            setUploadLoader(false);
        }
    };

    const clearSignature = () => {
        if (sigCanvas?.current) {
            sigCanvas.current.clear();
            setIsDrawing(false);
        }
    };

    const handleMouseDown = () => {
        setIsDrawing(true);
    };

    const tabsContent = [
        {
            tabComponent: <DrawStaff sigCanvas={sigCanvas} clearSignature={clearSignature} handleMouseDown={handleMouseDown} />,
            tab: 'Draw',
        },
        {
            tabComponent: <TypeStaff typedName={typedName} setTypedName={setTypedName} />,
            tab: 'Type',
        },
        {
            tabComponent: <SignatureFileUploader onFileChange={setUploadedFile} hideSaveDefault={true} />,
            tab: 'Upload',
        },
    ];

    return (
        <div style={{ width: 560 }}>
            <h2 className="modal-header-title-2">Signature</h2>

            <p className="select-document-desc" style={{ marginBottom: 24 }}>
                This signature will be automatically used in appointments and generated reports where a provider signature is required.
            </p>

            <SimpleTabs
                customTabPanel={'signature-tab-flow-box'}
                small={true}
                getTab={setProviderTab}
                tab={providerTab}
                defaultTab={tabEnums.DRAW}
                tabsLabels={signatureTabsLabels}
                tabsContent={tabsContent}
            />
            <p className="draw-info-text" style={{ margin: '0 0 12px 0' }}>
                By signing electronically, I agree to the clinic's terms and conditions.
            </p>

            <CreateChancel
                loader={uploadLoader || !!loadingDraw?.length}
                create={'Save'}
                chancel={'Cancel'}
                onCreate={providerTab === tabEnums.DRAW ? saveDraw : providerTab === tabEnums.UPLOAD ? saveUpload : saveTyped}
                onClose={close}
                buttonWidth="100%"
                disabled={disabled}
            />
        </div>
    );
};
